import { createContext, useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { InteractionRequiredAuthError } from "@azure/msal-browser";
import { HubConnectionBuilder } from "@microsoft/signalr";
import { loginRequest, silentRequest } from "../authConfig";
import { clientApiUrl, taskApiUrl, userApiUrl, notificationApiUrl, reportApiUrl } from "../utils/GetUrl";

export const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const { instance, accounts } = useMsal();

  const [token, setToken] = useState(null);
  const [userData, setUserData] = useState();
  const [authError, setAuthError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // SignalR connections. ViewForceData is wired up to the notification service
  // (negotiate → viewForceDataHub → "MessageReceived"). BudgetForecast and
  // MasterData remain stubbed until they are wired up the same way.
  const [connectionIdBudgetForecast, setConnectionIdBudgetForecast] = useState(null);
  const [connectionIdMasterData, setConnectionIdMasterData] = useState(null);
  const [connectionIdViewForceData, setConnectionIdViewForceData] = useState(null);
  const [connectionId, setConnectionId] = useState(null);

  const [pledgeRequired, setPledgeRequired] = useState(false);
  const [currentPolicy, setCurrentPolicy] = useState(null);

  const [generalNotifications, setGeneralNotification] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotifications, setUnreadNotifications] = useState(false);
  const [notificationsForBudgetForecast, setNotificationsForBudgetForecast] = useState([]);
  const [notificationsForMasterData, setNotificationsForMasterData] = useState([]);
  const [notificationsForViewForceData, setNotificationsForViewForceData] = useState([]);

  const toastRef = useRef();
  const latestNotificationIdRef = useRef(0);
  const hasInitialized = useRef(false);
  const tokenRef = useRef(null);
  const originalFetchRef = useRef(null);

  const user = userData?.[0];

  // ─── Core: get a fresh Entra access token ────────────────────────────────

  const acquireEntraToken = async () => {
    if (accounts.length === 0) return null;
    try {
      const result = await instance.acquireTokenSilent({
        ...silentRequest,
        account: accounts[0],
      });
      // Clear the redirect-attempt flag on success so future sessions start fresh.
      sessionStorage.removeItem('hampi_token_redirect_attempted');
      setToken(result.accessToken);
      return result.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Token needs user interaction (first login, consent, MFA, expired refresh).
        // Guard against infinite redirect loops: if we already tried a redirect this
        // session and still get InteractionRequired, there is a configuration problem
        // (most commonly REACT_APP_ENTRA_API_SCOPE references the wrong app Client ID).
        // Redirecting again would loop forever — surface the error instead.
        if (sessionStorage.getItem('hampi_token_redirect_attempted')) {
          sessionStorage.removeItem('hampi_token_redirect_attempted');
          throw new Error(
            "Unable to acquire an access token after login. " +
            "Verify that REACT_APP_ENTRA_API_SCOPE matches this app registration’s Client ID " +
            "(api://<client-id>/access_as_user). Contact your administrator if this persists."
          );
        }
        sessionStorage.setItem('hampi_token_redirect_attempted', '1');
        await instance.loginRedirect(loginRequest);
      } else {
        throw error;
      }
      return null;
    }
  };

  // ─── Identity bridge: link Entra OID to employee record ──────────────────
  // Uses user service API (localhost:7176)

  const syncIdentity = async (t) => {
    const response = await fetch(`${userApiUrl}/SyncIdentity`, {
      method: "POST",
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(
        error.message ||
          "Your Microsoft account is not linked to an employee record. Contact your administrator."
      );
    }
  };

  // ─── Load employee record from user service (localhost:7176) ─────────────

  const loadCurrentEmployee = async (t) => {
    const response = await fetch(`${userApiUrl}/GetCurrentEmployee`, {
      headers: { Authorization: `Bearer ${t}` },
    });
    if (!response.ok) throw new Error("Failed to load your employee profile.");
    const text = await response.text();
    if (!text?.trim()) throw new Error("Employee profile not found. Your account may not be linked yet — contact your administrator.");
    const employee = JSON.parse(text);
    setUserData([employee]);
    return employee;
  };

  // ─── Pledge: check if current user has accepted this month's policy ─────────

  const checkPledgeStatus = async (employee, t) => {
    try {
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const formatDT = (d) =>
        `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59);

      const policyRes = await fetch(
        `${userApiUrl}/GetPolicy?FromDate=${encodeURIComponent(formatDT(startOfMonth))}&Todate=${encodeURIComponent(formatDT(endOfMonth))}`
      );
      if (!policyRes.ok) return;
      const policyData = await policyRes.json();
      if (!policyData?.length) return;

      const policy = policyData.reduce((max, p) => (p.id > (max?.id || 0) ? p : max), null);

      const acceptRes = await fetch(
        `${userApiUrl}/GetPolicyAcceptance?Id=${employee.EM_ID}&FromDate=${encodeURIComponent(formatDT(startOfMonth))}&Todate=${encodeURIComponent(formatDT(endOfMonth))}`,
        { headers: { Authorization: `Bearer ${t}` } }
      );
      if (!acceptRes.ok) return;
      const acceptData = await acceptRes.json();

      const currentMonthYear = `${pad(now.getMonth() + 1)}-${now.getFullYear()}`;
      const hasAccepted = acceptData?.some((entry) => {
        const d = new Date(entry.createdDate);
        const entryMonthYear = `${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
        return (
          entry.userId?.toString() === employee.EM_ID?.toString() &&
          entry.acceptance === "true" &&
          entryMonthYear === currentMonthYear
        );
      });

      if (!hasAccepted) {
        setCurrentPolicy(policy);
        setPledgeRequired(true);
      }
    } catch (err) {
      console.error("Pledge check error:", err);
    }
  };

  const submitPledgeAcceptance = async (policyId) => {
    const t = tokenRef.current;
    const res = await fetch(`${userApiUrl}/AddPolicyAcceptance`, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${t}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        UserId: userData?.[0]?.EM_ID?.toString(),
        Acceptance: "true",
        PolicyId: policyId?.toString(),
      }),
    });
    if (!res.ok) throw new Error("Pledge acceptance failed");
    setPledgeRequired(false);
    setCurrentPolicy(null);
  };

  // ─── SignalR: upload result streams ───────────────────────────────────────
  // The notification service hosts a custom negotiate endpoint that needs a
  // hubName and returns { Url, AccessToken }. After an upload, the backend
  // pushes the result (success / error text) to the connected client via the
  // "MessageReceived" event. One connection per hub (ViewForce / Budget /
  // MasterData); the stored shape ({ connection }) matches each page's
  // `connectionId*?.connection?.connectionId` usage.

  const negotiateHub = async (hubName) => {
    const res = await fetch(
      `${notificationApiUrl}/negotiate?hubName=${hubName}`,
      { method: "POST" }
    );
    if (!res.ok) throw new Error(`negotiate failed (${res.status})`);
    const info = await res.json();
    return {
      url: info.Url ?? info.url,
      accessToken: info.AccessToken ?? info.accessToken,
    };
  };

  const connectHub = async (hubName, setConnection, pushNotification) => {
    try {
      console.log(`[SignalR ${hubName}] negotiating…`);
      const { url } = await negotiateHub(hubName);
      if (!url) {
        console.error(`SignalR (${hubName}): negotiate returned no url`);
        return;
      }

      const connection = new HubConnectionBuilder()
        // Re-negotiate on each (re)connect so the access token is always fresh.
        .withUrl(url, {
          accessTokenFactory: async () => (await negotiateHub(hubName)).accessToken,
        })
        .withAutomaticReconnect()
        .build();

      // Backend targets this client method with the result/error message string.
      connection.on("MessageReceived", (message) => {
        console.log(`[SignalR ${hubName}] MessageReceived:`, message);
        pushNotification((prev) => [...prev, message]);
      });

      await connection.start();
      console.log(`[SignalR ${hubName}] connected, connectionId=${connection.connectionId}`);
      setConnection({ connection });
    } catch (err) {
      console.error(`SignalR (${hubName}) connection error:`, err);
    }
  };

  const signalRConnectViewForceData = () =>
    connectHub("viewForceDataHub", setConnectionIdViewForceData, setNotificationsForViewForceData);
  const signalRConnectBudgetForecast = () =>
    connectHub("budgetforcastHub", setConnectionIdBudgetForecast, setNotificationsForBudgetForecast);
  const signalRConnectMasterData = () =>
    connectHub("masterdataHub", setConnectionIdMasterData, setNotificationsForMasterData);

  // ─── On login: acquire token → sync → load employee ──────────────────────

  const accountId = accounts[0]?.homeAccountId ?? '';

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    if (typeof window === "undefined" || originalFetchRef.current) return;

    const normalizeBaseUrl = (url) => url.replace(/\/+$/, "");
    const protectedBases = [userApiUrl, taskApiUrl, clientApiUrl, reportApiUrl, notificationApiUrl].map(normalizeBaseUrl);

    const shouldAttachAuth = (requestUrl) => {
      const normalizedUrl = normalizeBaseUrl(requestUrl);
      return protectedBases.some(
        (baseUrl) =>
          normalizedUrl === baseUrl ||
          normalizedUrl.startsWith(`${baseUrl}/`) ||
          normalizedUrl.startsWith(`${baseUrl}?`)
      );
    };

    originalFetchRef.current = window.fetch.bind(window);

    // Global concurrency limiter. The dashboard mounts many widgets that each
    // fire several API calls at once; that burst overloads the task function app,
    // which then returns 429 / drops CORS headers (surfacing as "CORS errors").
    // Cap the number of in-flight API requests app-wide so calls beyond the cap
    // wait their turn instead of flooding the backend. Only API calls
    // (protectedBases) are gated — auth/login/redirect flows are untouched.
    const MAX_CONCURRENT = 4;
    let activeCount = 0;
    const waitQueue = [];

    const acquireSlot = () => {
      if (activeCount < MAX_CONCURRENT) {
        activeCount += 1;
        return Promise.resolve();
      }
      // At capacity — wait until a slot is handed off on release.
      return new Promise((resolve) => waitQueue.push(resolve));
    };

    const releaseSlot = () => {
      const next = waitQueue.shift();
      if (next) {
        // Hand the freed slot directly to the next waiter (activeCount unchanged).
        next();
      } else {
        activeCount -= 1;
      }
    };

    window.fetch = async (input, init = {}) => {
      const requestUrl =
        typeof input === "string" || input instanceof URL ? input.toString() : input.url;

      if (!tokenRef.current || !shouldAttachAuth(requestUrl)) {
        return originalFetchRef.current(input, init);
      }

      const headers = new Headers(input instanceof Request ? input.headers : init.headers);
      if (!headers.has("Authorization")) {
        headers.set("Authorization", `Bearer ${tokenRef.current}`);
      }

      const doFetch = () =>
        input instanceof Request
          ? originalFetchRef.current(new Request(input, { ...init, headers }))
          : originalFetchRef.current(input, { ...init, headers });

      // Gate only real API calls so the dashboard burst can't overwhelm the
      // function app. The slot is always released in `finally`, so a failed or
      // slow request can never permanently starve the queue.
      await acquireSlot();
      try {
        // Retry on 429 (Too Many Requests). The task function app throttles
        // under the dashboard's concurrent burst; a short wait + retry recovers
        // transparently instead of surfacing a failed/blank call. Honour the
        // server's Retry-After header when present, otherwise exponential
        // backoff with jitter. The slot is held across retries, so repeated
        // 429s naturally reduce effective concurrency (backpressure).
        const MAX_RETRIES = 3;
        const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
        let response = await doFetch();
        for (let attempt = 0; response.status === 429 && attempt < MAX_RETRIES; attempt++) {
          const retryAfter = Number(response.headers.get("Retry-After"));
          const delayMs =
            Number.isFinite(retryAfter) && retryAfter > 0
              ? retryAfter * 1000
              : Math.min(8000, 500 * 2 ** attempt) + Math.random() * 250;
          await sleep(delayMs);
          response = await doFetch();
        }
        return response;
      } finally {
        releaseSlot();
      }
    };

    return () => {
      if (originalFetchRef.current) {
        window.fetch = originalFetchRef.current;
        originalFetchRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!accountId) {
      setIsLoading(false);
      return;
    }

    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initAuth = async () => {
      try {
        setAuthError(null);

        const t = await acquireEntraToken();
        if (!t) return;
        await syncIdentity(t);
        const employee = await loadCurrentEmployee(t);
        await checkPledgeStatus(employee, t);
        // Start the SignalR streams so upload results flow back to the UI.
        signalRConnectViewForceData();
        signalRConnectBudgetForecast();
        signalRConnectMasterData();
      } catch (err) {
        setAuthError(err.message);
        console.error("Auth init error:", err);
      } finally {
        setIsLoading(false);
      }
    };

    initAuth();
  }, [accountId]);

  // ─── General notifications (bell icon) ───────────────────────────────────

  const fetchGeneralNotifications = async () => {
    if (!user || !tokenRef.current) return;
    try {
      const userEmpID = user?.EM_ID;
      const response = await fetch(
        `${notificationApiUrl}/GetNotificationMessage?FirstCount=1&LastCount=10&Userid=${userEmpID}&sendto=${userEmpID}`,
        { headers: { Authorization: `Bearer ${tokenRef.current}`, "Content-Type": "application/json" } }
      );
      if (!response.ok) return;
      const data = await response.json();
      const normalized = data.map((n) => ({ ...n, createdDate: n.CreatedDate ?? n.createdDate }));
      setGeneralNotification(normalized);

      // Only show the unread dot for notifications newer than the last one seen.
      // The API returns the id field as `id`; keep NM_ID as a fallback for safety.
      const latestId = normalized.reduce((max, n) => Math.max(max, n.id ?? n.NM_ID ?? 0), 0);
      if (latestId > latestNotificationIdRef.current) {
        setUnreadNotifications(true);
      }
    } catch (err) {
      console.error("fetchGeneralNotifications error:", err);
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchGeneralNotifications();
    // Poll every 60 seconds for new notifications
    const interval = setInterval(fetchGeneralNotifications, 60_000);
    return () => clearInterval(interval);
  }, [user]);

  const markNotificationsAsRead = () => {
    // Record the highest notification ID seen so polling doesn't re-show the dot
    const latestId = generalNotifications.reduce((max, n) => Math.max(max, n.id ?? n.NM_ID ?? 0), 0);
    latestNotificationIdRef.current = latestId;
    setUnreadNotifications(false);
  };

  // ─── Auth expiry + logout ─────────────────────────────────────────────────

  const handleAuthExpiry = (response) => {
    if (response?.status === 401 || response?.status === 403) {
      setToken(null);
      setUserData(null);
      // If MSAL already has an account, re-authenticating will produce the same
      // token and loop forever. Surface the error instead of redirecting.
      if (accounts.length > 0) {
        setAuthError(
          "Your session was rejected by the server. Please sign out and contact your administrator if the problem persists."
        );
        return true;
      }
      instance.loginRedirect(loginRequest).catch(() => {});
      return true;
    }
    return false;
  };

  const logout = async (options = {}) => {
    setToken(null);
    setUserData(null);
    if (options.force) {
      await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
    } else {
      // Called programmatically (e.g. on a 401 response) — don't redirect to
      // Microsoft sign-out. Just clear local state and show an error so the
      // user can sign out manually if they want to.
      setAuthError(
        "One or more API calls were rejected (401). Your token may have expired or the backend may not yet be configured for this app registration. Please sign out and back in, or contact your administrator."
      );
    }
  };

  // ─── Context value ────────────────────────────────────────────────────────

  return (
    <AuthContext.Provider
      value={{
        token,
        userData,
        setUserData,
        authError,
        isLoading,
        acquireEntraToken,
        handleAuthExpiry,
        logout,
        fetchGeneralNotifications,
        setUnreadNotifications,
        markNotificationsAsRead,
        unreadNotifications,
        generalNotifications,
        setGeneralNotification,
        connectionId,
        connectionIdBudgetForecast,
        connectionIdViewForceData,
        connectionIdMasterData,
        setConnectionIdViewForceData,
        setConnectionIdMasterData,
        setConnectionId,
        setConnectionIdBudgetForecast,
        notifications,
        setNotifications,
        notificationsForMasterData,
        setNotificationsForMasterData,
        notificationsForViewForceData,
        setNotificationsForViewForceData,
        notificationsForBudgetForecast,
        setNotificationsForBudgetForecast,
        toastRef,
        pledgeRequired,
        currentPolicy,
        submitPledgeAcceptance,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
